import { get } from 'lodash';
import { FormikProps } from 'formik';
import BN from 'bn.js';

import { FormValues, OptionType } from 'types';
import { parseTokenAmount, removeTrailingZeros } from './amounts';

export function padTo2Digits(num) {
  return num.toString().padStart(2, '0');
}

export const nanoSecondsToDate = (dateTime, withTime = false) => {
  const date = new Date(Number(dateTime) / 1000000);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const dt = date.getUTCDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  if (withTime) {
    return `${year}-${padTo2Digits(month)}-${padTo2Digits(dt)} ${padTo2Digits(
      hours
    )}:${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
  }

  return `${padTo2Digits(dt)}.${padTo2Digits(month)}.${year}`;
};

function escapeRegExp(str: string) {
  return str.replace(/([.*+?^=!:${}()|[\]\/\\])/g, '\\$1');
}

const midInput = (number: number) =>{
  return   `^(0|([1-9]+\\d*))?(${escapeRegExp('.')}\\d{0,${number}})?$`;

}


export const numeric = (s: string, n = 5) => {
  return new RegExp(midInput(n)).test(s);
};

export const latin = (s: string) => /^[a-zA-Z0-9-._-]*$/.test(s);

export const getByValue = (arr: OptionType[], value: string) => {
  const result = arr.filter((item) => {
    return item.value === value;
  });
  return result.length > 0 ? result[0] : null;
};

export const isDecimalString = (value) => {
  const pattern = /^[0-9]*(|[.][0-9]{1,5})$/;
  return pattern.test(value);
};

export const isValidAmount = (value) => {
  return value && value !== '0' && isDecimalString(value);
};

export const getFieldProps = (
  formik: FormikProps<FormValues>,
  name: string
) => {
  const onChangeHandler = (n: string) => {
    if ( isNaN(Number(formik.values.amount )) && formik.values.token === 'OCC') {
      formik.setFieldValue(n, '0');
      return;
    }
    switch (n) {
      case 'amount':
        return (e: React.ChangeEvent<HTMLInputElement>) => {
          const { value, maxLength } = e.target;

          if (maxLength && value.length > maxLength) {
            return false;
          }
          if (numeric(value)) {
            formik.setFieldValue(n, value);
          }
        };
      default:
        return formik.handleChange;
    }
  };
  return {
    touched: get(formik, `touched.${name}`, false),
    error:
      formik.values.token === 'OCC' ? '' : get(formik, `errors.${name}`, ''),
    ...formik.getFieldProps(name),
    onChange: onChangeHandler(name)
  };
};

export const getStep = (idx: string, i: number, steps) => {
  const keys = Object.keys(steps);
  const values = Object.values(steps);
  let index = values.indexOf(idx);
  if ((i === -1 && index > 0) || (i === 1 && index < values.length - 1)) {
    index = index + i;
  }
  return steps[keys[index]];
};

export const timerCountdown = (duration) => {
  const timeleft = duration - Date.now();

  if (timeleft > 0) {
    const days = Math.floor(timeleft / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeleft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeleft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeleft % (1000 * 60)) / 1000);
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }
  return '0d 0h 0m 0s';
};

export const nanoToMilli = (timestamp) => Math.floor(timestamp / 1e6);

export const milliToNano = (timestamp) => Math.floor(timestamp * 1e6);

export const compareDates = (start, end) => {
  if (start < end) {
    return -1;
  } else if (start > end) {
    return 1;
  } else {
    return 0;
  }
};

export const getFontSize = (charLength) => {
  let baseSize = 64;

  if (charLength > 7) {
    baseSize = 60;
  }
  if (charLength > 12) {
    baseSize = 50;
  }

  if (charLength > 14) {
    baseSize = 45;
  }

  return baseSize;
};

export const onlyUnique = (projects) => {
  const map = new Map();
  const result = [];
  projects.forEach((item) => {
    const projects = item.tokens;
    for (const project of projects) {
      if (!map.has(project[0])) {
        map.set(project[0], true);
        result.push(project);
      }
    }
  });
  return result;
};

export const uniqueIds = (id, index, self) => self.indexOf(id) === index;

export const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1);

export const sumNumberHelper = (arr, field = 'value', precision = 5) => {
  return removeTrailingZeros(
    arr.reduce((sum, a) => Number(sum) + Number(a[field]), 0).toFixed(precision)
  ).toString();
};

export const sumBigHelper = (arr, field = 'balance') =>
  arr.length > 0
    ? arr
        .reduce(
          (sum, a) =>
            new BN(parseTokenAmount(sum, 0)).add(
              new BN(parseTokenAmount(a[field], 0))
            ),
          '0'
        )
        .toString()
    : '0';

export const downloadFile = ({ data, fileName, fileType }) => {
  const blob = new Blob([data], { type: fileType });

  const a = document.createElement('a');
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  const clickEvt = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true
  });
  a.dispatchEvent(clickEvt);
  a.remove();
};

export const calculationCell = (item) => {
  const current_stage = item.stages[item.key.stage_id];
  const periods = current_stage.periods;
  const voting_ends_at = current_stage.voting_ends_at;
  let last_completed_period = current_stage.last_completed_period;
  const period_ends_at = periods[last_completed_period].ends_at;
  const diffTime = compareDates(nanoToMilli(voting_ends_at), Date.now());

  let valueCell = '---';

  if (diffTime === -1) {
    valueCell = 'Ongoing Resolution';
  }

  if (diffTime === 1) {
    const diffTimePeriods = compareDates(
      nanoToMilli(period_ends_at),
      Date.now()
    );

    if (diffTimePeriods === -1) {
      if (last_completed_period === 4) {
        last_completed_period = 4;
      } else {
        last_completed_period++;
      }
    }

    switch (last_completed_period) {
      case 0:
        valueCell = 'Ongoing Validation';
        break;
      case 1:
        valueCell = 'Ongoing Challenge';
        break;
      case 2:
      case 3:
      case 4:
        valueCell = 'Ongoing Escalation';
        break;
    }
  }

  const valueDate = period_ends_at / 1e6;

  return { valueCell, valueDate };
};

export const getProjectIdFromToken = (tokenKey: string) => tokenKey.split(':')[1];

export const isFungibleToken = (tokenKey: string) => tokenKey.startsWith('ft');

export const isNonFungibleToken = (tokenKey: string) => tokenKey.startsWith('nft');

export const decodeB64 = (s) => Buffer.from(s, 'base64').toString('utf-8');
